/**
 * Settings · History Replay (v1 · 2026-08-19).
 *
 * Read-only audit surface for scrubbing back through Price Notification
 * state changes. Timeline events are reconstructed on the backend from
 * fields already stored on each notification (uploaded_at, status_history,
 * approved_at, send_history, sent_at) — zero new writes.
 *
 * Role gate enforced by the backend (Administrator + Owner + Auditor).
 */
import { useEffect, useMemo, useState } from "react";
import {
  ClipboardText, MagnifyingGlass, ArrowClockwise, CalendarBlank,
  CheckCircle, XCircle, Upload, PaperPlaneTilt, ArrowRight, Warning,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";


function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
    });
  } catch { return iso; }
}

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const EVENT_META = {
  uploaded:     { color: "text-slate-600",   bg: "bg-slate-100",    icon: Upload,           label: "Uploaded" },
  parsed:       { color: "text-indigo-600",  bg: "bg-indigo-50",    icon: ClipboardText,    label: "Parsed" },
  approved:     { color: "text-emerald-700", bg: "bg-emerald-50",   icon: CheckCircle,      label: "Approved" },
  rack_posted:  { color: "text-emerald-700", bg: "bg-emerald-50",   icon: ArrowRight,       label: "Rack posted" },
  sent:         { color: "text-sky-700",     bg: "bg-sky-50",       icon: PaperPlaneTilt,   label: "Driver notice sent" },
  send_failed:  { color: "text-rose-700",    bg: "bg-rose-50",      icon: XCircle,          label: "Send failed" },
  status_change:{ color: "text-slate-600",   bg: "bg-slate-100",    icon: ArrowRight,       label: "State change" },
};

function eventMeta(type) {
  if (EVENT_META[type]) return EVENT_META[type];
  if (type?.startsWith("status:")) {
    const to = type.slice("status:".length);
    return {
      ...EVENT_META.status_change,
      label: `State → ${to}`,
    };
  }
  return { ...EVENT_META.status_change, label: type };
}


function defaultRange() {
  // Last 30 days by default.
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { from: iso(from), to: iso(to) };
}


export default function History() {
  const [range, setRange] = useState(defaultRange);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (range.from) params.from = range.from;
      if (range.to)   params.to   = range.to;
      if (q.trim())   params.q    = q.trim();
      const { data } = await api.get("/history/price-notifications", { params });
      setItems(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = useMemo(() => {
    const total = items.length;
    const sent = items.filter((i) => i.sent_at).length;
    const approvedOnly = items.filter((i) => i.approved_at && !i.sent_at).length;
    return { total, sent, approvedOnly };
  }, [items]);

  return (
    <div className="space-y-6" data-testid="history-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
          <ClipboardText size={14} weight="bold" />
          <span>Auditor · Read-only</span>
        </div>
        <h1 className="text-2xl font-semibold text-slate-900 mt-1">History Replay</h1>
        <p className="text-sm text-slate-600 mt-1">
          Scrub back through Price Notification state changes. Timeline
          events are reconstructed from existing audit fields — nothing
          new is written when you open this page.
        </p>
      </div>

      {/* Filter bar */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">From</label>
            <div className="relative">
              <CalendarBlank size={16} className="absolute left-2 top-2.5 text-slate-400" />
              <input
                type="date"
                value={range.from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                className="w-full rounded border border-slate-300 pl-8 pr-2 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                data-testid="history-from-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">To</label>
            <div className="relative">
              <CalendarBlank size={16} className="absolute left-2 top-2.5 text-slate-400" />
              <input
                type="date"
                value={range.to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                className="w-full rounded border border-slate-300 pl-8 pr-2 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                data-testid="history-to-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Supplier search</label>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-2 top-2.5 text-slate-400" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="e.g. Suncor"
                className="w-full rounded border border-slate-300 pl-8 pr-2 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                data-testid="history-supplier-input"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:bg-slate-300"
              data-testid="history-apply-btn"
            >
              <ArrowClockwise size={16} weight="bold" />
              {loading ? "Loading…" : "Apply"}
            </button>
          </div>
        </div>
      </div>

      {/* Summary counters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryCard label="Notifications" value={summary.total} testid="history-count-total" />
        <SummaryCard label="Sent to drivers" value={summary.sent} testid="history-count-sent" />
        <SummaryCard label="Approved · not yet sent" value={summary.approvedOnly} testid="history-count-approved-only" />
      </div>

      {/* Error */}
      {error && (
        <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 flex items-start gap-2" data-testid="history-error">
          <Warning size={18} className="flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* List */}
      {!error && (
        <div className="space-y-3" data-testid="history-list">
          {loading && items.length === 0 && (
            <div className="rounded border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Loading history…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="rounded border border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="history-empty">
              No notifications in this range.
            </div>
          )}
          {items.map((item) => (
            <HistoryRow
              key={item.id}
              item={item}
              open={expanded === item.id}
              onToggle={() => setExpanded(expanded === item.id ? null : item.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function SummaryCard({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-900 mt-1 tabular-nums">{value}</div>
    </div>
  );
}


function HistoryRow({ item, open, onToggle }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={`history-row-${item.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
        data-testid={`history-row-toggle-${item.id}`}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900">{item.supplier || "—"}</span>
            <span className="text-xs text-slate-500">·</span>
            <span className="text-sm text-slate-700">Effective {fmtDate(item.effective_date)}</span>
            <StateBadge state={item.business_state || item.status} />
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {item.event_count} event{item.event_count === 1 ? "" : "s"}
            {item.first_at && ` · from ${fmtDT(item.first_at)}`}
            {item.last_at && item.last_at !== item.first_at && ` to ${fmtDT(item.last_at)}`}
          </div>
        </div>
        <div className="text-slate-400 text-xs">{open ? "Hide" : "Show timeline"}</div>
      </button>
      {open && (
        <div className="border-t border-slate-200 px-4 py-4 bg-slate-50/50">
          <Timeline events={item.timeline || []} />
        </div>
      )}
    </div>
  );
}


function StateBadge({ state }) {
  if (!state) return null;
  const palette = {
    approved:    "bg-emerald-100 text-emerald-800",
    sent:        "bg-sky-100 text-sky-800",
    distributed: "bg-sky-100 text-sky-800",
    parsed:      "bg-indigo-100 text-indigo-800",
    draft:       "bg-slate-100 text-slate-700",
    rejected:    "bg-rose-100 text-rose-800",
    superseded:  "bg-amber-100 text-amber-800",
  };
  const cls = palette[state] || "bg-slate-100 text-slate-700";
  return (
    <span className={`ml-2 inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${cls}`}>
      {state}
    </span>
  );
}


function Timeline({ events }) {
  if (!events || events.length === 0) {
    return <div className="text-sm text-slate-500">No events reconstructed.</div>;
  }
  return (
    <ol className="relative space-y-4">
      {events.map((e, i) => {
        const meta = eventMeta(e.type);
        const Icon = meta.icon;
        return (
          <li key={`${e.at}-${e.type}-${i}`} className="flex gap-3" data-testid={`history-event-${i}`}>
            <div className={`flex-shrink-0 rounded-full p-1.5 ${meta.bg}`}>
              <Icon size={16} className={meta.color} weight="bold" />
            </div>
            <div className="flex-1 pb-1">
              <div className="flex items-baseline gap-2">
                <span className={`text-sm font-medium ${meta.color}`}>{meta.label}</span>
                <span className="text-xs text-slate-500">{fmtDT(e.at)}</span>
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                {e.actor && <span>by <span className="font-medium">{e.actor}</span></span>}
                {e.note && <span>{e.actor ? " · " : ""}{e.note}</span>}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
